import React, { FC } from 'react';

export interface WritingSession {
  id: string;
  text: string;
  timestamp: Date;
  duration: number;
  wordCount: number;
  title: string;
  goal?: string;
}

interface WritingHistoryProps {
  sessions: WritingSession[];
}

const WritingHistory: FC<WritingHistoryProps> = ({ sessions }) => {
  if (sessions.length === 0) {
    return null;
  }

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (date: Date) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    // Check if the date is today
    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    }
    
    // Check if the date is yesterday
    if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }
    
    // Otherwise return the formatted date
    return date.toLocaleDateString([], { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
    });
  };

  // Group sessions by date
  const groupedSessions: { [key: string]: WritingSession[] } = {};
  sessions.forEach(session => {
    const dateKey = session.timestamp.toDateString();
    if (!groupedSessions[dateKey]) {
      groupedSessions[dateKey] = [];
    }
    groupedSessions[dateKey].push(session);
  });

  // Sort dates from oldest to newest
  const sortedDates = Object.keys(groupedSessions).sort((a, b) => 
    new Date(a).getTime() - new Date(b).getTime()
  );

  return (
    <div className="chat-history pb-4">
      {sortedDates.map(dateKey => {
        // Sort sessions within each date from oldest to newest
        const dateSessions = [...groupedSessions[dateKey]].sort((a, b) => 
          a.timestamp.getTime() - b.timestamp.getTime()
        );
        
        return (
          <div key={dateKey} className="mb-6">
            <div className="text-center mb-4">
              <span className="px-3 py-1 bg-gray-200 rounded-full text-xs font-medium text-gray-600">
                {formatDate(new Date(dateKey))}
              </span>
            </div>
            <div className="space-y-4">
              {dateSessions.map(session => (
                <div key={session.id} className="chat-bubble px-4">
                  <div className="bg-indigo-50 rounded-lg p-4 shadow-sm">
                    <div className="mb-2 pb-2 border-b border-indigo-100">
                      <h3 className="font-medium text-indigo-700">{session.title}</h3>
                      {session.goal && (
                        <p className="text-xs text-indigo-600 mt-1">Goal: {session.goal}</p>
                      )}
                    </div>
                    <div className="whitespace-pre-wrap">{session.text}</div>
                  </div>
                  <div className="flex justify-between items-center text-xs text-gray-500 px-2 pt-1">
                    <span>{formatTime(session.timestamp)}</span>
                    <span className="flex space-x-3">
                      <span>{session.wordCount} words</span>
                      <span>{session.duration}s</span>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default WritingHistory; 